import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { askMatchAI } from '../api';
import { toast } from '../toast';

export default function MatchChat({ matchId, homeName, awayName }: {
  matchId: string;
  homeName?: string;
  awayName?: string;
}) {
  const [q, setQ] = useState('');
  const [history, setHistory] = useState<{ q: string; a: string }[]>([]);

  const mut = useMutation({
    mutationFn: (question: string) => askMatchAI(matchId, question),
    onSuccess: (data, question) => {
      if (data.answer) setHistory((h) => [...h, { q: question, a: data.answer! }]);
      else toast.error('No answer.');
      setQ('');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const text = q.trim();
    if (!text) return;
    mut.mutate(text);
  }
  function askChip(text: string) {
    if (mut.isPending) return;
    setQ(text);
    mut.mutate(text);
  }

  const home = homeName ?? 'home';
  const away = awayName ?? 'away';
  const suggestions = [
    `Why is ${home} favored?`,
    `What's ${home}'s key weakness?`,
    `Recent form for ${away}?`,
    `Likely scoreline and why?`,
    `Key player to watch?`,
  ];

  return (
    <section className="card p-4 ring-1 ring-cyan-500/20">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-cyan-400">Ask the AI about this match</h2>
        <span className="text-[10px] text-dim">Llama 3.3 70B</span>
      </div>
      <p className="text-[11px] text-dim mb-2.5">
        Tactical, historical, statistical — anything. One to three sentence answers.
      </p>

      {history.length === 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {suggestions.map((s) => (
            <button
              key={s} type="button"
              onClick={() => askChip(s)}
              disabled={mut.isPending}
              className="text-[11px] px-2 py-1 rounded-md bg-cyan-500/10 text-cyan-400 ring-1 ring-cyan-500/20 hover:bg-cyan-500/20 disabled:opacity-40"
            >{s}</button>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <ol className="space-y-3 mb-3 max-h-80 overflow-y-auto">
          {history.map((h, i) => (
            <li key={i} className="space-y-1.5">
              <div className="text-[11px] text-dim">You: <span className="text-chalk">{h.q}</span></div>
              <div className="text-sm leading-snug rounded-md bg-cyan-500/5 ring-1 ring-cyan-500/10 px-2.5 py-1.5">{h.a}</div>
            </li>
          ))}
        </ol>
      )}

      <form onSubmit={submit} className="flex gap-2">
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={`e.g. Why does ${home} have the edge?`}
          maxLength={400}
          className="flex-1 bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk"
          data-allow-select
        />
        <button
          type="submit"
          disabled={!q.trim() || mut.isPending}
          className="h-9 px-3 rounded-lg bg-cyan-500 text-ink-950 text-xs font-semibold disabled:opacity-50"
        >{mut.isPending ? '…' : 'Ask'}</button>
      </form>
    </section>
  );
}
